// Dual Monitor Detection and Window Management

import { getHangingProtocolByModalityAndBodyPart } from "./helper";
import { getCentreProxyRs, primeCentreProxies } from "./centreProxy";
import { isReportingOnOtherScreen } from "./workstationLayout";

interface ScreenInfo {
  left: number;
  top: number;
  width: number;
  height: number;
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
  isPrimary?: boolean;
}

interface MonitorConfig {
  hasMultipleMonitors: boolean;
  screens: ScreenInfo[];
  primaryScreen: ScreenInfo;
  secondaryScreen?: ScreenInfo;
  /** The screen the calling browser window is currently displayed on. */
  currentScreen?: ScreenInfo;
  /**
   * The screen the calling window is NOT sitting on. This is where a *reporting* window
   * goes under the 'reporting-other' layout, and the default home of the viewer.
   * Undefined when only a single screen is available.
   */
  viewerScreen?: ScreenInfo;
  /**
   * Where a viewer launch should actually land once the user's workstation layout
   * preference is taken into account -- see resolveViewerPlacement(). Equals
   * `viewerScreen` for everyone on the default layout.
   */
  viewerPlacementScreen?: ScreenInfo;
}

interface LaunchResult {
  success: boolean;
  viewerWindow: Window | null;
  message: string;
}

class MonitorManager {
  private screens: ScreenInfo[] = [];
  private isExtendedMode = false;
  private primaryScreen: ScreenInfo | null = null;
  private secondaryScreen: ScreenInfo | null = null;
  private currentScreen: ScreenInfo | null = null;

  // --- Viewer buffer-tab queue (VIEWER_BUFFER_TABS) ---
  // When VIEWER_BUFFER_TABS > 1, viewer launches share a fixed pool of named
  // windows (viewer_1..viewer_N). Each slot remembers the study it shows AND the
  // actual window handle, so we can tell which tabs are still open. Slot choice
  // prefers, in order:
  //   1. the slot already showing this study in a still-open tab (reuse it);
  //   2. any slot whose tab is closed / never opened (fill the gap first);
  //   3. otherwise the least-recently-used slot gets recycled.
  // This means closing a viewer frees its slot for the next study instead of
  // blindly recycling a tab the radiologist is still using.
  private viewerSlots: { studyUID: string | null; win: Window | null; lastUsed: number }[] = [];
  private viewerUseTick = 0;

  constructor() {
    // Warm the centre -> proxy map ahead of the first study click so viewer
    // launches can route to the study's own centre cache proxy without an
    // await before window.open (which the browser would popup-block).
    primeCentreProxies().catch(() => {});
  }

  /**
   * Build the `&dicomProxy=` fragment pointing a viewer tab at the study's own
   * centre cache proxy, resolved from the order's po_center_code. Returns '' when
   * the centre is unknown or has no proxy — the viewer then falls back to the
   * primary dicom_proxy (default.js). Correctness never depends on this: a proxy
   * miss forwards to central, which federates. It only wins the local-cache hit.
   */
  private dicomProxyParam(orderDetails: any): string {
    const rs = getCentreProxyRs(orderDetails?.pacs_order?.po_center_code);
    return rs ? `&dicomProxy=${encodeURIComponent(rs)}` : '';
  }

  private getViewerBufferCount(): number {
    const raw = (window as any).ENV_CONFIG?.VIEWER_BUFFER_TABS;
    const n = Math.floor(Number(raw));
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  private isViewerSlotAlive(slot: { win: Window | null }): boolean {
    return !!slot.win && !slot.win.closed;
  }

  /**
   * Choose which buffer slot a study should open in.
   * Returns the slot index, or null when buffering is disabled (count <= 1) so
   * the caller keeps its legacy target ('viewer' / '_blank').
   */
  private acquireViewerSlot(studyUID: string): number | null {
    const count = this.getViewerBufferCount();
    if (count <= 1) return null;

    // (Re)size the pool if the configured count changed.
    if (this.viewerSlots.length !== count) {
      this.viewerSlots = Array.from({ length: count }, () => ({
        studyUID: null,
        win: null,
        lastUsed: 0,
      }));
      this.viewerUseTick = 0;
    }

    // 1. Already open in a live tab -> reuse that exact tab.
    const sameAlive = this.viewerSlots.findIndex(
      s => s.studyUID === studyUID && this.isViewerSlotAlive(s)
    );
    if (sameAlive !== -1) return sameAlive;

    // 2. Fill an empty / closed slot before evicting anything still open.
    const free = this.viewerSlots.findIndex(s => !this.isViewerSlotAlive(s));
    if (free !== -1) {
      this.viewerSlots[free].studyUID = studyUID;
      return free;
    }

    // 3. All tabs still open -> recycle the least-recently-used one.
    let lru = 0;
    for (let i = 1; i < this.viewerSlots.length; i++) {
      if (this.viewerSlots[i].lastUsed < this.viewerSlots[lru].lastUsed) lru = i;
    }
    this.viewerSlots[lru].studyUID = studyUID;
    return lru;
  }

  /** Record the opened window handle for a slot and mark it most-recently-used. */
  private recordViewerWindow(slotIndex: number | null, win: Window | null): void {
    if (slotIndex === null) return;
    const slot = this.viewerSlots[slotIndex];
    if (!slot) return;
    if (win) slot.win = win;
    slot.lastUsed = ++this.viewerUseTick;
  }

  async detectScreens(): Promise<MonitorConfig> {
    try {
      // Modern Screen API (Chrome 86+)
      if ('getScreenDetails' in window) {
        const permission = await navigator.permissions.query({ name: 'window-management' as PermissionName });

        if (permission.state !== 'denied') {
          const screenDetails = await (window as any).getScreenDetails();
          this.screens = screenDetails.screens;
          this.isExtendedMode = this.screens.length > 1;
          this.primaryScreen = this.screens.find(s => s.isPrimary) || this.screens[0];
          this.secondaryScreen = this.screens.find(s => !s.isPrimary);
          // Where the calling window actually is. This -- not `isPrimary` -- is what
          // decides where the viewer goes; see resolveViewerScreen().
          this.currentScreen = screenDetails.currentScreen || this.primaryScreen;

          return this.getConfig();
        }
      }
    } catch (error) {
      console.warn('Modern Screen API not available:', error);
    }

    return this.detectLegacy();
  }

  private detectLegacy(): MonitorConfig {
    const { width, height, availWidth, availHeight } = window.screen;

    this.isExtendedMode = availWidth > width || availHeight > height;
    this.primaryScreen = {
      left: 0, top: 0, width, height,
      availLeft: 0, availTop: 0,
      availWidth: Math.min(availWidth, width),
      availHeight: Math.min(availHeight, height),
      isPrimary: true
    };

    if (this.isExtendedMode) {
      this.secondaryScreen = {
        left: width, top: 0,
        width: availWidth - width, height,
        availLeft: width, availTop: 0,
        availWidth: availWidth - width, availHeight,
        isPrimary: false
      };
    }

    // The legacy path has no way to tell which screen the window is on; assume the
    // primary so this branch behaves exactly as it always has.
    this.currentScreen = this.primaryScreen;

    return this.getConfig();
  }

  /**
   * Screen identity. `currentScreen` should be reference-equal to one of the
   * entries in `screens`, but geometry is compared as well so a browser that
   * hands back a fresh object per call still matches.
   */
  private isSameScreen(a: ScreenInfo | null, b: ScreenInfo | null): boolean {
    if (!a || !b) return false;
    if (a === b) return true;
    return a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;
  }

  /**
   * The screen the viewer should be placed on: the one the calling window is NOT on.
   *
   * This used to be hard-coded to the OS-designated non-primary screen, which broke
   * for radiologists who keep the PACS browser window on their second monitor -- the
   * viewer popup was placed on that same monitor, on top of the list/report tab that
   * launched it, while the primary monitor sat empty.
   *
   * With more than two screens a free primary is preferred (so someone sitting on the
   * primary keeps the previous behaviour), otherwise the first free screen wins.
   * Falls back to the old primary/secondary split when the current screen is unknown.
   */
  private resolveViewerScreen(): ScreenInfo | null {
    if (!this.isExtendedMode) return null;

    const candidates = this.screens.filter(s => !this.isSameScreen(s, this.currentScreen));
    if (!candidates.length) return this.secondaryScreen;

    return candidates.find(s => s.isPrimary) || candidates[0];
  }

  /**
   * Is this window the reporting editor (/oo-report)?
   *
   * Under the 'reporting-other' layout the reporting window has been pushed to the far
   * monitor, so a viewer launched from it wants the opposite placement to one launched
   * from a study list, which lives on the monitor the user is sitting at. Detected from
   * the route rather than passed down by every call site, so the reporting page's
   * auto-launch, its "Launch Viewer" button and any future launcher inside it all get
   * the right answer without remembering to flag themselves.
   */
  private isReportingWindow(): boolean {
    try {
      return (window.location?.pathname || '').replace(/\/+$/, '') === '/oo-report';
    } catch {
      return false;
    }
  }

  /**
   * The screen a viewer launch should actually open on, honouring the user's workstation
   * layout preference.
   *
   * 'viewer-other' (default): images belong on the monitor the user is NOT at. Every
   * launcher -- study list, or a reporting tab in that same window -- sits on the monitor
   * they are at, so "away from the opener" is exactly right.
   *
   * 'reporting-other': images belong on the monitor the user IS at. Which screen that is
   * depends on who opened the viewer:
   *   - the reporting window has itself moved to the far monitor, so away-from-opener puts
   *     the viewer back on the seated monitor -- unchanged from before;
   *   - a study list "Open Viewer" click comes from the window the user is sitting at, so
   *     away-from-opener would throw the images onto the far monitor, the opposite of what
   *     the preference asks for. The current screen is the right target there.
   */
  private resolveViewerPlacement(): ScreenInfo | null {
    const awayFromOpener = this.resolveViewerScreen();
    if (!awayFromOpener) return null;
    if (!isReportingOnOtherScreen() || this.isReportingWindow()) return awayFromOpener;
    return this.currentScreen || awayFromOpener;
  }

  private getConfig(): MonitorConfig {
    return {
      hasMultipleMonitors: this.isExtendedMode,
      screens: this.isExtendedMode ? [this.primaryScreen!, this.secondaryScreen!] : [this.primaryScreen!],
      primaryScreen: this.primaryScreen!,
      secondaryScreen: this.secondaryScreen,
      currentScreen: this.currentScreen || undefined,
      viewerScreen: this.resolveViewerScreen() || undefined,
      viewerPlacementScreen: this.resolveViewerPlacement() || undefined
    };
  }

  /** Centred rectangle for a popup on `screen`, defaulting to a 95% inset. */
  private getWindowBounds(
    screen: ScreenInfo,
    width?: number,
    height?: number
  ): { width: number; height: number; left: number; top: number } {
    const w = width || screen.availWidth * 0.95;
    const h = height || screen.availHeight * 0.95;
    return {
      width: Math.round(w),
      height: Math.round(h),
      left: Math.round(screen.availLeft + (screen.availWidth - w) / 2),
      top: Math.round(screen.availTop + (screen.availHeight - h) / 2),
    };
  }

  private getWindowFeatures(screen: ScreenInfo, width?: number, height?: number): string {
    const { width: w, height: h, left, top } = this.getWindowBounds(screen, width, height);

    return [
      `width=${w}`,
      `height=${h}`,
      `left=${left}`,
      `top=${top}`,
      'resizable=yes,scrollbars=yes,status=yes'
    ].join(',');
  }

  /** Does this point fall inside the screen's rectangle? */
  private isPointOnScreen(screen: ScreenInfo, x: number, y: number): boolean {
    return (
      x >= screen.left && x < screen.left + screen.width &&
      y >= screen.top && y < screen.top + screen.height
    );
  }

  /**
   * Pull an already-open viewer window onto the screen it is now supposed to be on.
   *
   * Viewer launches reuse a fixed window name ('viewer', or a buffer slot), and browsers
   * apply window features only when a window is *created* -- a reused one stays wherever
   * it was. Without this, changing the workstation layout preference would not take
   * effect on the viewer until the radiologist closed the window by hand.
   *
   * Only a window on the *wrong* screen is moved, so a viewer the user has deliberately
   * resized or nudged on the correct monitor is left exactly as they left it. Everything
   * here is best-effort: moveTo/resizeTo are no-ops on a plain tab and throw on a window
   * that is closing, neither of which should disturb the launch.
   */
  private repositionViewerWindow(
    win: Window | null,
    target: ScreenInfo,
    fillScreen: boolean
  ): void {
    if (!win || win.closed) return;

    try {
      // A freshly created popup is already placed by the features string; screenX/screenY
      // of a reused window tell us where it actually sits.
      if (this.isPointOnScreen(target, win.screenX, win.screenY)) return;

      const bounds = fillScreen
        ? this.getWindowBounds(target, target.availWidth, target.availHeight)
        : this.getWindowBounds(target);
      win.moveTo(bounds.left, bounds.top);
      win.resizeTo(bounds.width, bounds.height);
    } catch (error) {
      // Popup blocked from moving (plain tab), or the handle went away mid-call.
      console.warn('Could not reposition the viewer window:', error);
    }
  }

  async launchDualMonitorSetup(studyUID: string, orderDetails): Promise<LaunchResult> {
    const { pacs_order={} } = orderDetails;
    const {po_modality, po_body_part, po_diag_desc} = pacs_order;

    try {
      const config = await this.detectScreens();
      // --- Dual Viewer Mode (client-specific) ---
      // When DUAL_VIEWER_MODE is enabled, open the viewer on BOTH monitors.
      // Monitor 1 (primary) gets series 1 & 2 via @fyzks/dualMonitor1 protocol.
      // Monitor 2 (secondary) gets series 3 & 4 via @fyzks/dualMonitor2 protocol.
      // Each window receives a ?screenNumber= param (1 or 2) so the viewer can
      if ((window as any).ENV_CONFIG?.DUAL_VIEWER_MODE && config.hasMultipleMonitors) {
        return this.launchDualViewerMode(studyUID, orderDetails);
      }

      let viewerUrl = `/viewer/viewer?StudyInstanceUIDs=${studyUID}`;
      if (orderDetails?.pacs_order?.po_his_status === 'CANCELLED' || orderDetails?.order_workflow?.ow_pacs_status === 'CANCELLED') {
        viewerUrl += '&is_cancelled=true';
      }
      let hpId;
      if (po_modality && po_body_part) {
        hpId = getHangingProtocolByModalityAndBodyPart(po_modality, po_body_part, po_diag_desc)
      }

      if (hpId) {
        viewerUrl += `&hangingProtocolId=${hpId}`;
      }

      viewerUrl += this.dicomProxyParam(orderDetails);

      // Pick a buffer slot. null when VIEWER_BUFFER_TABS <= 1, in which case the
      // legacy per-branch target is used and nothing is tracked.
      const slotIndex = this.acquireViewerSlot(studyUID);
      const bufferTarget = slotIndex === null ? null : `viewer_${slotIndex + 1}`;

      // Place the viewer per the user's workstation layout: on the far monitor by
      // default, or on the monitor they are sitting at when they have asked to keep
      // images in front of them (resolveViewerPlacement). Either way it never lands on
      // top of the window that launched it unless that is what the preference asks for.
      const target = config.viewerPlacementScreen;
      if (config.hasMultipleMonitors && target) {
        // 'reporting-other' users read images on their reading monitor full-bleed, so the
        // viewer fills that screen's whole work area rather than the default 95% inset.
        const features = isReportingOnOtherScreen()
          ? this.getWindowFeatures(target, target.availWidth, target.availHeight)
          : this.getWindowFeatures(target);
        const viewerWindow = window.open(viewerUrl, bufferTarget ?? 'viewer', features);
        // Reused windows ignore `features`; drag one back if the preference has since
        // moved where the images belong.
        this.repositionViewerWindow(viewerWindow, target, isReportingOnOtherScreen());
        this.recordViewerWindow(slotIndex, viewerWindow);
        return {
          success: true,
          viewerWindow,
          message: this.isSameScreen(target, this.currentScreen)
            ? 'Viewer opened on this monitor'
            : 'Viewer opened on the other monitor'
        };
      }

      // Single monitor: legacy opens a fresh tab ('_blank'); buffered mode reuses
      // the chosen named tab instead.
      const viewerWindow = window.open(viewerUrl, bufferTarget ?? '_blank');
      this.recordViewerWindow(slotIndex, viewerWindow);
      return {
        success: true,
        viewerWindow,
        message: 'Single monitor - opened in new tab'
      };

    } catch (error) {
      let fallbackUrl = `/viewer/viewer?StudyInstanceUIDs=${studyUID}`;
      if (orderDetails?.pacs_order?.po_his_status === 'CANCELLED' || orderDetails?.order_workflow?.ow_pacs_status === 'CANCELLED') {
        fallbackUrl += '&is_cancelled=true';
      }
      fallbackUrl += this.dicomProxyParam(orderDetails);
      const viewerWindow = window.open(fallbackUrl, '_blank');
      return {
        success: false,
        viewerWindow,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Opens the DICOM viewer on both monitors simultaneously (DUAL_VIEWER_MODE).
   *
   * - Monitor 1 (primary screen):  screenNumber=1, protocol @fyzks/dualMonitor1  → series 1 & 2
   * - Monitor 2 (secondary screen): screenNumber=2, protocol @fyzks/dualMonitor2 → series 3 & 4
   *
   * Falls back to two separate tabs when only one monitor is detected.
   */
  private async launchDualViewerMode(studyUID: string, orderDetails: any = {}): Promise<LaunchResult> {
    const PROTOCOL_MONITOR_1 = '@fyzks/dualMonitor1';
    const PROTOCOL_MONITOR_2 = '@fyzks/dualMonitor2';

    let isCancelledParam = '';
    if (orderDetails?.pacs_order?.po_his_status === 'CANCELLED' || orderDetails?.order_workflow?.ow_pacs_status === 'CANCELLED') {
      isCancelledParam = '&is_cancelled=true';
    }

    const proxyParam = this.dicomProxyParam(orderDetails);

    const monitor1Url =
      `/viewer/viewer?StudyInstanceUIDs=${studyUID}` +
      `&screenNumber=1` +
      isCancelledParam +
      proxyParam +
      `&hangingProtocolId=${encodeURIComponent(PROTOCOL_MONITOR_1)}`;

    const monitor2Url =
      `/viewer/viewer?StudyInstanceUIDs=${studyUID}` +
      `&screenNumber=2` +
      isCancelledParam +
      proxyParam +
      `&hangingProtocolId=${encodeURIComponent(PROTOCOL_MONITOR_2)}`;

    try {
      const config = await this.detectScreens();

      if (config.hasMultipleMonitors && config.primaryScreen && config.secondaryScreen) {
        // Open monitor 1 (primary) and monitor 2 (secondary) simultaneously
        const win1 = window.open(
          monitor1Url,
          'fyzks_viewer_monitor1',
          this.getWindowFeatures(config.primaryScreen)
        );
        const win2 = window.open(
          monitor2Url,
          'fyzks_viewer_monitor2',
          this.getWindowFeatures(config.secondaryScreen)
        );
        return {
          success: true,
          // Return the primary monitor window as the representative handle
          viewerWindow: win1,
          message: 'Dual viewer mode: viewer opened on both monitors',
        };
      }

      // Single-monitor fallback: open both in separate tabs
      window.open(monitor1Url, 'fyzks_viewer_monitor1');
      const win2 = window.open(monitor2Url, 'fyzks_viewer_monitor2');
      return {
        success: true,
        viewerWindow: win2,
        message: 'Dual viewer mode (single monitor fallback): opened two tabs',
      };
    } catch (error) {
      // Last-resort fallback
      const viewerWindow = window.open(monitor1Url, '_blank');
      return {
        success: false,
        viewerWindow,
        message: `Dual viewer mode error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Open the viewer filtered to a chosen set of series (from the "Select Series
   * to Open" popup). A single plain tab — the dual-monitor / buffer-slot logic
   * isn't applied here (series opens have always been a one-off new tab). Carries
   * the same `&dicomProxy=` routing as the full-study opens so the study loads
   * from its own centre's cache proxy.
   */
  openSeriesViewer(studyUID: string, seriesUIDs: string[] | string, orderDetails: any = {}): Window | null {
    const series = Array.isArray(seriesUIDs) ? seriesUIDs.join(',') : seriesUIDs;
    let url = `/viewer/viewer?StudyInstanceUIDs=${studyUID}`;
    if (series) url += `&SeriesInstanceUIDs=${series}`;
    if (orderDetails?.pacs_order?.po_his_status === 'CANCELLED' || orderDetails?.order_workflow?.ow_pacs_status === 'CANCELLED') {
      url += '&is_cancelled=true';
    }
    url += this.dicomProxyParam(orderDetails);
    return window.open(url, '_blank');
  }

  /**
   * Open the reporting editor (/oo-report).
   *
   * Default layout: a plain tab in the current window, i.e. on the monitor the user is
   * already looking at -- unchanged from before this preference existed.
   *
   * 'reporting-other': its own window on the other monitor, which is what leaves the
   * monitor the user is sitting at free for the images (a viewer launched from this
   * reporting window goes to the screen its opener is not on; one launched from a study
   * list targets the seated screen directly -- see resolveViewerPlacement).
   *
   * Always opens with '_blank' rather than a fixed window name: radiologists routinely
   * keep several reports open at once (see the concurrent-report limit), so reporting must
   * never recycle an existing window the way the viewer buffer slots do.
   */
  async openReportingWindow(url: string): Promise<Window | null> {
    if (!isReportingOnOtherScreen()) {
      return window.open(url, '_blank');
    }

    try {
      const config = await this.detectScreens();
      if (config.hasMultipleMonitors && config.viewerScreen) {
        const win = window.open(url, '_blank', this.getWindowFeatures(config.viewerScreen));
        // A popup blocked by the browser returns null; drop through to a plain tab so the
        // click is never silently swallowed.
        if (win) return win;
      }
    } catch (error) {
      console.warn('Could not place the reporting window; falling back to a tab:', error);
    }

    return window.open(url, '_blank');
  }

  async getMonitorInfo(): Promise<MonitorConfig & { configuration: string }> {
    const config = await this.detectScreens();
    return {
      ...config,
      configuration: config.hasMultipleMonitors ? 'Extended' : 'Single Monitor'
    };
  }
}

// Utility functions
const monitorManager = new MonitorManager();

export const launchViewerOnDualMonitor = async (studyUID: string, orderDetails = {}): Promise<Window | null> => {
  const result = await monitorManager.launchDualMonitorSetup(studyUID, orderDetails);
  if (!result.success) console.warn(result.message);
  return result.viewerWindow;
};

export const openSeriesViewer = (
  studyUID: string,
  seriesUIDs: string[] | string,
  orderDetails: any = {}
): Window | null => monitorManager.openSeriesViewer(studyUID, seriesUIDs, orderDetails);

export const openReportingWindow = (url: string): Promise<Window | null> =>
  monitorManager.openReportingWindow(url);

export const checkMonitorSetup = async (): Promise<MonitorConfig & { configuration: string }> => {
  return await monitorManager.getMonitorInfo();
};

export { MonitorManager };
export type { MonitorConfig, LaunchResult, ScreenInfo };
